import React, { memo, useCallback, useMemo, useState, useRef } from 'react';
import { SwiperClass } from 'swiper/react';
import useGlobalState from '@hooks/useGlobalState';
import ShortformItem2 from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/ShortformItem2';
import ShortformSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/ShortformSection';
import { PromotionStyleClip } from '@wconcept/wconcept-common/models/renewal/promotion';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import useLinkTo from '@hooks/useLinkTo';
import { useRelatedProductModalStore } from '@store/relatedProductModalStore';
import useModalManager from '@hooks/useModalManager';
import { QueryStatus } from '@hooks/useVisiblePromotionProducts';
import useGa4Event from '@hooks/useGa4Event';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import RelatedProducts from '../../common/RelatedProducts';

type DirectQueryStatus = {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  error: unknown;
  data: Product[] | undefined;
};

interface ShortformModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  title: string;
  subTitle: string;
  isPaused?: boolean;
  styleClips: PromotionStyleClip[];
  landingUrl: string;
  setIsPaused?: (value: boolean) => void;
  queryStatusMap?: Map<string, QueryStatus>;
  directQueryStatusMap?: Map<string, DirectQueryStatus>;
  contentId?: string;
  promotionTitle?: string;
  promotionId?: number;
}

function ShortformModule({
  bgType,
  visualTokens,
  isDesktop,
  title,
  subTitle,
  isPaused,
  styleClips,
  setIsPaused,
  landingUrl,
  queryStatusMap,
  directQueryStatusMap,
  contentId,
  promotionTitle,
  promotionId,
}: ShortformModuleProps) {
  const selectedShortformRef = useRef<PromotionStyleClip | null>(null);
  const { linkTo } = useLinkTo();
  const { appInfo, assistData } = useGlobalState();
  const { setProductIds } = useRelatedProductModalStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    sendGa4SelectItem,
    sendGa4AddToWishProduct,
    sendGa4ButtonClick,
    sendGa4BannerClick,
  } = useGa4Event();
  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();
  const { openRelatedPrdModal } = useModalManager({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        area: 'promotion_shortform_moreitem_myheart',
        page: 'promotion',
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        variable: title,
        pageNo:
          styleClips.findIndex(v => v.id === selectedShortformRef.current?.id) +
          1,
        listNo: index ? Number(index) + 1 : undefined,
        ecommercePath: '프로모션',
        myheartYn: true,
        referralNM:
          selectedShortformRef.current?.profileInfo?.profile?.nickName ||
          undefined,
        referralID:
          selectedShortformRef.current?.profileInfo?.profile?.profileSeqNo?.toString(),
        affiliation:
          selectedShortformRef.current?.profileInfo?.profile
            ?.creatorStatusCd === 'Y'
            ? 'wcreator'
            : undefined,
        referralCat: '스타일링',
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        area: 'promotion_shortform_moreitem_myheart',
        page: 'promotion',
        issueCd: promotionId?.toString(),
        variable: title,
        pageNo:
          styleClips.findIndex(v => v.id === selectedShortformRef.current?.id) +
          1,
        issueNm: promotionTitle,
        listNo: index ? Number(index) + 1 : undefined,
        myheartYn: false,
        ecommercePath: '프로모션',
        referralID:
          selectedShortformRef.current?.profileInfo?.profile?.profileSeqNo?.toString(),
        referralNM:
          selectedShortformRef.current?.profileInfo?.profile?.nickName ||
          undefined,
        affiliation:
          selectedShortformRef.current?.profileInfo?.profile
            ?.creatorStatusCd === 'Y'
            ? 'wreator'
            : undefined,
        referralCat: '스타일링',
      });
    },
  });

  const getIsGnbType = useCallback(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return urlSearchParams.get('gnbType') === 'Y';
  }, []);

  const getItem = useCallback(
    () =>
      styleClips.slice(0, 10).map((v, styleClipIndex) => {
        // 현재 styleClip의 itemCdList에 해당하는 상품들을 순서대로 가져오기
        const relatedProducts = () => {
          if (!v.itemCdList?.length) return [];
          return v.itemCdList
            .map(itemCd =>
              queryStatusMap
                ?.get(contentId?.toString() || '')
                ?.productsMap?.get(itemCd)
            )
            .filter((product): product is Product => !!product);
        };
        return (
          <ShortformItem2
            key={v.id}
            rs="150"
            userImgSrc={v?.profileInfo?.profile?.profileImageUrl}
            userName={v.profileInfo?.profile?.nickName || ''}
            token={
              v?.vodInfo?.token ? { playAuthToken: v.vodInfo.token } : undefined
            }
            onClickProfile={() => {
              // promotion_shortform_profile_button
              sendGa4ButtonClick({
                area: 'promotion_shortform_profile_button',
                page: 'promotion',
                issueCd: contentId?.toString(),
                issueNm: promotionTitle,
                text: v.profileInfo?.profile?.nickName || '',
                listNo: Number(styleClipIndex + 1),
                variable: title,
              });
              linkTo.push({
                url: v.profileInfo?.profile?.landingUrl || '',
              });
            }}
            vid={v.vodInfo?.id && 'vid'}
            videoSrc={
              !v?.vodInfo?.token && v.vodInfo?.playBackUrl
                ? v.vodInfo?.playBackUrl
                : undefined
            }
            isPaused={isPaused}
            videoPoster={v.vodInfo?.thumbnailUrl}
            desc={v.description}
            // relatedPrd={getRelatedPrdRender(v.relatedPrd ?? [], v.product ?? [])}
            relatedPrd={RelatedProducts({
              isDesktop,
              isShow: true,
              rs: '124',
              total: relatedProducts().length,
              products: relatedProducts().slice(0, 3),
              visibleCount: 3,
              onClick: (product, index) => {
                sendGa4SelectItem({
                  product,
                  area: 'promotion_shortform_item',
                  page: 'promotion',
                  issueCd: promotionId?.toString(),
                  issueNm: promotionTitle,
                  listNo: Number(index + 1),
                  pageNo: Number(styleClipIndex + 1),
                  referralCat: '스타일링',
                  referralID: v?.profileInfo?.profile?.profileSeqNo?.toString(),
                  referralNM: v?.profileInfo?.profile?.nickName || undefined,
                  affiliation:
                    v?.profileInfo?.profile?.creatorStatusCd === 'Y'
                      ? 'wcreator'
                      : undefined,
                  ecommercePath: '프로모션',
                  variable: title,
                });
                linkTo.push({
                  url: product.landingUrl || '',
                });
              },
              onMoreClick() {
                document.body.classList.add('no-scroll');
                sendGa4ButtonClick({
                  area: 'promotion_shortform_moreitem_button',
                  page: 'promotion',
                  pageNo: Number(styleClipIndex + 1),
                  issueCd: promotionId?.toString(),
                  issueNm: promotionTitle,
                  text: '더보기',
                  variable: title,
                });
                setIsPaused?.(true);
                if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                  document.location.href = 'wconceptkr://actionBarOff';
                }
                selectedShortformRef.current = v;
                openRelatedPrdModal({
                  onClose: () => {
                    selectedShortformRef.current = null;
                    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                      document.location.href = 'wconceptkr://actionBarOn';
                    }
                    document.body.classList.remove('no-scroll');
                    setIsPaused?.(false);
                  },
                  placement: isDesktop ? 'right' : 'bottom',
                  className: 'is-renewal',
                  value: {
                    type: 'shortform',
                  },
                  products: relatedProducts(),
                  onClickProduct: (product, index) => {
                    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                      document.location.href = 'wconceptkr://actionBarOn';
                    }
                    // promotion_shortform_modal_item
                    document.body.classList.remove('no-scroll');
                    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                      document.location.href = 'wconceptkr://actionBarOff';
                    }
                    sendGa4SelectItem({
                      product,
                      area: 'promotion_shortform_moreitem_item',
                      page: 'promotion',
                      ecommercePath: '프로모션',
                      variable: title,
                      referralCat: '스타일링',
                      referralID:
                        v?.profileInfo?.profile?.profileSeqNo?.toString(),
                      referralNM:
                        v?.profileInfo?.profile?.nickName || undefined,
                      affiliation:
                        v?.profileInfo?.profile?.creatorStatusCd === 'Y'
                          ? 'wcreator'
                          : undefined,
                      pageNo: Number(styleClipIndex + 1),
                      issueCd: promotionId?.toString(),
                      issueNm: promotionTitle,
                      listNo: Number(index + 1),
                    });
                  },
                });
              },
            })}
            isDesktop={isDesktop}
            onClick={() => {
              // promotion_shortform_banner
              sendGa4BannerClick({
                area: 'promotion_shortform_banner',
                page: 'promotion',
                issueCd: v.profileInfo.profile.profileSeqNo.toString(),
                issueNm: v.profileInfo?.profile?.nickName || '',
                text: v.profileInfo?.profile?.nickName || '',
                bannerType: '스타일링',
                bannerForm: v.vodInfo ? 'video' : 'image',
                listNo: Number(styleClipIndex + 1),
                variable: title,
              });
              if (isDesktop) {
                linkTo.push({
                  url: v.pcLandingUrl || '',
                });
              } else {
                linkTo.push({
                  url: v.landingUrl || '',
                });
              }
            }}
          />
        );
      }),
    [
      styleClips,
      isPaused,
      isDesktop,
      queryStatusMap,
      contentId,
      sendGa4ButtonClick,
      promotionTitle,
      linkTo,
      sendGa4SelectItem,
      promotionId,
      setIsPaused,
      appInfo?.isNativeAppBoth,
      getIsGnbType,
      openRelatedPrdModal,
      sendGa4BannerClick,
    ]
  );

  const { presetBgType, customColorsLite } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'twoField',
  });

  const sectionColorType = customColorsLite ?? presetBgType;

  if (styleClips?.length < 4) {
    return null;
  }

  return (
    <ShortformSection
      isDesktop={isDesktop}
      title={title}
      subTitle={subTitle}
      colorType={sectionColorType}
      items={getItem()}
      isPaused={isPaused}
      onSlideChange={(swiper: SwiperClass) => {
        setActiveIndex(swiper.activeIndex);
      }}
      onClickMore={() => {
        const selectedShortformLandingUrl =
          selectedShortformRef.current?.landingUrl;
        let bannerType = 'etc';
        if (selectedShortformLandingUrl?.includes('/event')) {
          bannerType = 'issue';
        }
        if (selectedShortformLandingUrl?.includes('/brand')) {
          bannerType = 'brand';
        }
        if (selectedShortformLandingUrl?.includes('/promotion')) {
          bannerType = 'promotion';
        }
        if (selectedShortformLandingUrl?.includes('/category')) {
          bannerType = 'category';
        }
        sendGa4ButtonClick({
          area: 'promotion_shortform_more_button',
          page: 'promotion',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          text: '숏폼 더보기',
          variable: title,
          bannerType,
        });
        linkTo.push({
          url: landingUrl,
        });
      }}
      isLoaded={
        directQueryStatusMap?.get(contentId?.toString() || '')?.isSuccess
      }
    />
  );
}

export default memo(ShortformModule);
